/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  TextField,
  Button,
  Snackbar
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  FolderShared as FolderSharedIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ChevronLeft,
  ChevronRight,
  Article as ArticleIcon
} from '@mui/icons-material';
import FolderTree from './FolderTree';
import FileList from './FileList';
import DirectorySearch from './DirectorySearch';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Cache with TTL for folder contents
class DirectoryCache {
  constructor(ttlMs = 5 * 60 * 1000) { // 5 minutes default TTL
    this.cache = new Map();
    this.ttl = ttlMs;
  }

  getKey(driveId, folderId, params = {}) {
    return `${driveId}:${folderId}:${JSON.stringify(params)}`;
  }

  get(driveId, folderId, params = {}) {
    const key = this.getKey(driveId, folderId, params);
    const cached = this.cache.get(key);

    if (!cached) return null;

    if (Date.now() - cached.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  set(driveId, folderId, data, params = {}) {
    const key = this.getKey(driveId, folderId, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  invalidate(driveId, folderId = null) {
    if (folderId) {
      // Invalidate specific folder
      const keysToDelete = [];
      for (const key of this.cache.keys()) {
        if (key.startsWith(`${driveId}:${folderId}:`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      // Invalidate entire drive
      const keysToDelete = [];
      for (const key of this.cache.keys()) {
        if (key.startsWith(`${driveId}:`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
    }
  }

  clear() {
    this.cache.clear();
  }
}

const directoryCache = new DirectoryCache();

// Request deduplication to prevent duplicate API calls
const pendingRequests = new Map();

const makeCachedRequest = async (key, requestFn) => {
  // Check cache first
  if (directoryCache.cache.has(key)) {
    const cached = directoryCache.cache.get(key);
    if (Date.now() - cached.timestamp <= directoryCache.ttl) {
      return cached.data;
    }
  }

  // Check if request is already pending
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  // Make new request
  const promise = requestFn();
  pendingRequests.set(key, promise);

  try {
    const result = await promise;
    return result;
  } finally {
    pendingRequests.delete(key);
  }
};

 
 
 
 
 
 
 
 
// eslint-disable-next-line max-lines-per-function
export default function DirectoryNavigator({ selectedDrive }) {
  const { user } = useAuth();
  const [currentFolderId, setCurrentFolderId] = useState(selectedDrive?.id);
  const [folderContents, setFolderContents] = useState({ folders: [], files: [] });
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'modified', 'size', 'type'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [filterType, setFilterType] = useState('all'); // 'all', 'folders', 'files', specific mime types
  const [showTreeView, setShowTreeView] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [fipdDialogOpen, setFipdDialogOpen] = useState(false);
  const [fipdFormData, setFipdFormData] = useState({
    name: '',
    description: '',
    documentType: 'general',
    googleDriveFileId: ''
  });
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [savingToFipd, setSavingToFipd] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Performance optimization: Memoize filtered and sorted data
  const processedData = useMemo(() => {
    const folders = folderContents.folders || [];
    const files = folderContents.files || [];
    let items = [...folders, ...files];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      if (filterType === 'folders') {
        items = items.filter(item => item.type === 'folder');
      } else if (filterType === 'files') {
        items = items.filter(item => item.type === 'file');
      } else {
        // Specific mime type filter
        items = items.filter(item => item.mimeType?.includes(filterType));
      }
    }

    // Apply sorting
    items.sort((a, b) => {
      let comparison;

      // Always show folders first unless specifically filtering
      if (filterType === 'all') {
        if (a.type === 'folder' && b.type === 'file') return -1;
        if (a.type === 'file' && b.type === 'folder') return 1;
      }

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'modified':
          comparison = new Date(a.modifiedTime || 0) - new Date(b.modifiedTime || 0);
          break;
        case 'size':
          comparison = (a.size || 0) - (b.size || 0);
          break;
        case 'type':
          comparison = (a.mimeType || '').localeCompare(b.mimeType || '');
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return {
      folders: items.filter(item => item.type === 'folder'),
      files: items.filter(item => item.type === 'file'),
      allItems: items
    };
  }, [folderContents, searchQuery, filterType, sortBy, sortOrder]);

  // Load folder contents with caching
  const loadFolderContents = useCallback(async (driveId, folderId, params = {}) => {
    const cacheKey = directoryCache.getKey(driveId, folderId, params);

    try {
      setLoading(true);
      setError(null);

      const result = await makeCachedRequest(cacheKey, async () => {
        const data = await api.getFolderContents(driveId, folderId, {
          pageSize: 100,
          ...params
        });

        // Cache the result
        directoryCache.set(driveId, folderId, data, params);
        return data;
      });

      console.log(`[DirectoryNavigator] Folder contents loaded for driveId=${driveId}, folderId=${folderId}:`, {
        folders: result?.folders?.length || 0,
        files: result?.files?.length || 0,
        data: result
      });
      setFolderContents(result);
      return result;
    } catch (err) {
      console.error('Error loading folder contents:', err);
      setError(err.message || 'Failed to load folder contents');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load breadcrumb path
  const loadBreadcrumbPath = useCallback(async (driveId, folderId) => {
    try {
      const result = await api.getFolderPath(driveId, folderId);
      setBreadcrumbPath(result.path || []);
      return result;
    } catch (err) {
      console.error('Error loading breadcrumb path:', err);
      setBreadcrumbPath([]);
      return null;
    }
  }, []);

  // Navigate to folder
  const navigateToFolder = useCallback(async (folderId, _addToHistory = true) => {
    if (!selectedDrive) return;

    // Update folder ID first
    setCurrentFolderId(folderId);

    // Load both folder contents and breadcrumb path in parallel
    await Promise.all([
      loadFolderContents(selectedDrive.id, folderId),
      loadBreadcrumbPath(selectedDrive.id, folderId)
    ]);

    // Clear search when navigating
    if (searchMode) {
      setSearchMode(false);
      setSearchQuery('');
    }
  }, [selectedDrive, loadFolderContents, loadBreadcrumbPath, searchMode]);

  // Initialize with drive root
  useEffect(() => {
    if (selectedDrive && !currentFolderId) {
      setCurrentFolderId(selectedDrive.id);
      loadFolderContents(selectedDrive.id, selectedDrive.id);
      loadBreadcrumbPath(selectedDrive.id, selectedDrive.id);
    }
  }, [selectedDrive, currentFolderId, loadFolderContents, loadBreadcrumbPath]);

  // Check if user is system admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?.email) {
        try {
          // For now, assume faanaworks.com users are admins
          // This should be replaced with proper role checking
          const isAdmin = user.email.endsWith('@faanaworks.com');
          setIsSystemAdmin(isAdmin);

          // TODO: Replace with proper API call when getUserEntityRole is implemented
          // const roles = await api.getUserEntityRole(user.email, entityId);
          // setIsSystemAdmin(roles.isSystemAdmin || false);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsSystemAdmin(false);
        }
      }
    };
    checkAdminStatus();
  }, [user]);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = useCallback((pathItem) => {
    navigateToFolder(pathItem.id);
  }, [navigateToFolder]);

  // Handle folder double-click
  const handleFolderOpen = useCallback((folder) => {
    navigateToFolder(folder.id);
  }, [navigateToFolder]);

  // Handle search
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSearchMode(!!query);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (selectedDrive && currentFolderId) {
      // Clear cache for current folder
      directoryCache.invalidate(selectedDrive.id, currentFolderId);

      // Reload contents
      navigateToFolder(currentFolderId, false);
    }
  }, [selectedDrive, currentFolderId, navigateToFolder]);

  // Handle sort change
  const handleSortChange = useCallback((newSortBy) => {
    if (newSortBy === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  }, [sortBy, sortOrder]);

  // Context menu handlers
  const handleContextMenu = useCallback((event, item) => {
    event.preventDefault();
    console.log('Context menu item:', item);
    console.log('Is system admin:', isSystemAdmin);
    console.log('Item type:', item?.type);
    console.log('Item mimeType:', item?.mimeType);
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  }, [isSystemAdmin]);

  const handleContextMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedItem(null);
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSaveToFipd = useCallback(async () => {
    if (!fipdFormData.name || !fipdFormData.documentType) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      setSavingToFipd(true);
      await api.manageFIPDDocument(fipdFormData);
      showSnackbar('Document added to IP Catalog successfully', 'success');
      setFipdDialogOpen(false);
      setFipdFormData({
        name: '',
        description: '',
        documentType: 'general',
        googleDriveFileId: ''
      });
    } catch (error) {
      console.error('Error saving to IP Catalog:', error);
      showSnackbar('Failed to add document to IP Catalog', 'error');
    } finally {
      setSavingToFipd(false);
    }
  }, [fipdFormData, showSnackbar]);

  const handleItemAction = useCallback((action) => {
    if (!selectedItem) return;

    switch (action) {
      case 'open':
        if (selectedItem.type === 'folder') {
          handleFolderOpen(selectedItem);
        } else {
          window.open(selectedItem.webViewLink, '_blank');
        }
        break;
      case 'openInNewTab':
        window.open(selectedItem.webViewLink, '_blank');
        break;
      case 'addToFipd':
        // Prepare form data with file information
        setFipdFormData({
          name: selectedItem.name.replace(/\.[^/.]+$/, ''), // Remove file extension
          description: '',
          documentType: 'general',
          googleDriveFileId: selectedItem.id
        });
        setFipdDialogOpen(true);
        break;
      default:
        break;
    }

    handleContextMenuClose();
  }, [selectedItem, handleFolderOpen, handleContextMenuClose]);

  if (!selectedDrive) {
    return (
      <Alert severity="info">
        Please select a shared drive to browse its contents.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with breadcrumbs and controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderSharedIcon />
            Directory Navigator
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={viewMode === 'list' ? 'Switch to Grid View' : 'Switch to List View'}>
              <IconButton onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}>
                {viewMode === 'list' ? <ViewModuleIcon /> : <ViewListIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={showTreeView ? 'Hide Folder Tree' : 'Show Folder Tree'}>
              <IconButton onClick={() => setShowTreeView(!showTreeView)}>
                {showTreeView ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {breadcrumbPath.map((pathItem, index) => (
            <Link
              key={pathItem.id}
              component="button"
              variant="body2"
              onClick={() => handleBreadcrumbClick(pathItem)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                color: index === breadcrumbPath.length - 1 ? 'text.primary' : 'primary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {pathItem.isRoot ? <HomeIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
              {pathItem.name}
            </Link>
          ))}
        </Breadcrumbs>

        {/* Search and filters */}
        <DirectorySearch
          searchQuery={searchQuery}
          onSearch={handleSearch}
          filterType={filterType}
          onFilterChange={setFilterType}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      </Paper>

      {/* Main content area */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
        {/* Tree view sidebar */}
        {showTreeView && (
          <Paper sx={{ width: 300, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.primary">Folder Tree</Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <FolderTree
                driveId={selectedDrive.id}
                currentFolderId={currentFolderId}
                onFolderSelect={navigateToFolder}
                cache={directoryCache}
              />
            </Box>
          </Paper>
        )}

        {/* File list */}
        <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" color="text.primary">
                {searchMode ? `Search Results (${processedData.allItems.length})` : 'Contents'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`${processedData.folders.length} folders`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${processedData.files.length} files`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            ) : (
              <FileList
                items={processedData.allItems}
                viewMode={viewMode}
                onFolderOpen={handleFolderOpen}
                onContextMenu={handleContextMenu}
                loading={loading}
              />
            )}
          </Box>
        </Paper>
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleContextMenuClose}
      >
        <MenuItem onClick={() => handleItemAction('open')}>
          <ListItemText>
            {selectedItem?.type === 'folder' ? 'Open Folder' : 'Open File'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleItemAction('openInNewTab')}>
          <ListItemText>Open in Google Drive</ListItemText>
        </MenuItem>
        {isSystemAdmin && selectedItem?.type === 'file' &&
         (selectedItem?.mimeType === 'application/vnd.google-apps.document' ||
          selectedItem?.mimeType === 'application/pdf' ||
          selectedItem?.mimeType?.includes('document')) && (
          <MenuItem onClick={() => handleItemAction('addToFipd')}>
            <ListItemIcon>
              <ArticleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add to IP Catalog</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* FIPD Dialog */}
      <Dialog
        open={fipdDialogOpen}
        onClose={() => setFipdDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to IP Catalog</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Document Name"
            fullWidth
            value={fipdFormData.name}
            onChange={(e) => setFipdFormData({ ...fipdFormData, name: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={fipdFormData.description}
            onChange={(e) => setFipdFormData({ ...fipdFormData, description: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Document Type</InputLabel>
            <Select
              value={fipdFormData.documentType}
              onChange={(e) => setFipdFormData({ ...fipdFormData, documentType: e.target.value })}
              label="Document Type"
            >
              <SelectMenuItem value="policy">Policy Document</SelectMenuItem>
              <SelectMenuItem value="template">Template</SelectMenuItem>
              <SelectMenuItem value="guide">Guide</SelectMenuItem>
              <SelectMenuItem value="framework">Framework</SelectMenuItem>
              <SelectMenuItem value="methodology">Methodology</SelectMenuItem>
              <SelectMenuItem value="standard">Standard</SelectMenuItem>
              <SelectMenuItem value="general">General</SelectMenuItem>
              <SelectMenuItem value="other">Other</SelectMenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFipdDialogOpen(false)} disabled={savingToFipd}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveToFipd}
            variant="contained"
            disabled={savingToFipd}
          >
            {savingToFipd ? 'Saving...' : 'Add to Library'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}